import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import { MsalProvider } from '@azure/msal-react';
import { HelmetProvider } from 'react-helmet-async';
import {
  msalConfig,
  federatedMsalConfig,
  isExternalIdAccount,
  isFederatedAccount,
} from './utils/authConfig';
import { setMsalInstance, setFederatedInstance } from './utils/api';
import { runRedirectBridgeIfLanding } from './utils/redirect-bridge-guard';
import { TeamsProvider } from './context/TeamsContext';
import './index.css';
import App, { preloadMarketingRoute } from './App';

// ── External ID instance (CIAM) ────────────────────────────
// The Phase 1 instance — Google / email-password / invitations.
// Unchanged: same clientId, authority, redirectUri, MsalProvider
// wiring. Every existing user path runs through exactly this.
const msalInstance = new PublicClientApplication(msalConfig);

// Register the instance with the API client before <App /> mounts
// (utils/api.js holds the reference for the acquireTokenSilent
// flow on every authenticated /api/* call).
setMsalInstance(msalInstance);

// ── Federated (direct-Entra) instance — Stage 2 dual-MSAL ───
// The SECOND PublicClientApplication — the workforce SPA app reg
// signing client M365 users in DIRECTLY against the Entra
// /organizations endpoint. Constructed ONLY when the build
// injected REACT_APP_FEDERATED_* (federatedMsalConfig non-null).
// In the dormant prod build it is null and this whole path is
// inert — the frontend mirror of the functions-flex.bicep GO-LIVE
// gate. See authConfig.js.
const federatedInstance = federatedMsalConfig
  ? new PublicClientApplication(federatedMsalConfig)
  : null;
if (federatedInstance) {
  setFederatedInstance(federatedInstance);
}

// Set an instance's active account from its OWN authority's
// accounts only. The account cache is shared across both
// instances on one origin (FINDING gotcha #1), so getAllAccounts()
// MUST be filtered — never trust [0] unfiltered.
function activateOwnAccount(instance, belongsToInstance) {
  const own = instance.getAllAccounts().filter(belongsToInstance);
  if (own.length > 0) {
    instance.setActiveAccount(own[0]);
  }
}

async function bootstrap() {
  // ── MSAL v5 redirect bridge — MUST run before either initialize() ──
  // When MSAL renews a token silently it opens a HIDDEN IFRAME to
  // /authorize?prompt=none, and Entra redirects that iframe to our
  // redirectUri (/ for External ID, /login/org for federated) with
  // #code=…&state=…. The main window then waits on a BroadcastChannel
  // for THIS document to post the response back. msal-browser v5 does
  // not do that for us (and initialising MSAL in the framed landing
  // throws block_iframe_reload), so: if this document is that landing,
  // broadcast and stop — never boot the SPA inside the hidden iframe.
  // Without this, every silent renew timed out (redirect_bridge_timeout)
  // and the app showed "Unable to load your profile" with zero API
  // calls each morning a tab had idled past its refresh token
  // (2026-09-11; utils/redirect-bridge-guard.js has the full account).
  if (await runRedirectBridgeIfLanding(window, broadcastResponseToMainFrame)) {
    return;
  }

  // External ID — initialise, set the active account (filtered to
  // External ID accounts), keep it current on login. The External
  // ID redirect return is drained by <MsalProvider> below; we do
  // NOT call handleRedirectPromise on it here (that would be a
  // double-call) — behaviour unchanged from Phase 1.
  await msalInstance.initialize();
  activateOwnAccount(msalInstance, isExternalIdAccount);
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  // Federated instance — initialise it and drain ITS redirect
  // return. It is NOT wrapped by an MsalProvider (FINDING gotcha
  // #8 — driven imperatively), so unlike the External ID instance
  // it must call handleRedirectPromise itself. Awaited BEFORE
  // render, so a /login/org return is consumed by the federated
  // instance first — no race with the External ID instance on the
  // URL hash (FINDING gotcha #4; the two paths also use distinct
  // redirect URIs, /login vs /login/org).
  if (federatedInstance) {
    await federatedInstance.initialize();
    await federatedInstance.handleRedirectPromise();
    activateOwnAccount(federatedInstance, isFederatedAccount);
    federatedInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        federatedInstance.setActiveAccount(event.payload.account);
      }
    });
  }

  // A prerendered marketing page is already on screen. Load its page
  // module first so React's first render is the page itself, not the
  // loading screen (see utils/lazyWithPreload.js). A failed chunk load is
  // not fatal: React.lazy retries it and shows the loading screen.
  try {
    await preloadMarketingRoute(window.location.pathname);
  } catch {
    // fall through
  }

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <TeamsProvider>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </TeamsProvider>
      </HelmetProvider>
    </React.StrictMode>
  );
}

bootstrap();
